import { injectable } from 'inversify';
import {
  ParsedConnectionStats,
  StatsReportItem,
} from 'webrtc-issue-detector/dist/types';
import type {
  ParsedInboundAudioStreamCompositeStats,
  ParsedInboundVideoStreamCompositeStats,
  ParsedOutboundAudioStreamCompositeStats,
  ParsedOutboundVideoStreamCompositeStats,
} from './types';
import { findStatsBySsrc } from '../../qoe/helpers';

@injectable()
export default class StatsHandler {
  readonly #connectionHandlers = new Map<string, (stats: ParsedConnectionStats) => void>();

  readonly #inboundVideoHandlers = new Map<string, (stats: ParsedInboundVideoStreamCompositeStats) => void>();

  readonly #inboundAudioHandlers = new Map<string, (stats: ParsedInboundAudioStreamCompositeStats) => void>();

  readonly #outboundVideoHandlers = new Map<string, (stats: ParsedOutboundVideoStreamCompositeStats[]) => void>();

  readonly #outboundAudioHandlers = new Map<string, (stats: ParsedOutboundAudioStreamCompositeStats) => void>();

  handle(payload: StatsReportItem[]): void {
    payload.forEach(({ id, stats }) => {
      this.#connectionHandlers.get(id)?.(stats.connection);
      stats.video.inbound.forEach((stat) => this.#inboundVideoHandlers.get(stat.trackIdentifier)?.({
        inbound: stat,
        connection: stats.connection,
      }));

      stats.audio.inbound.forEach((stat) => this.#inboundAudioHandlers.get(stat.trackIdentifier)?.({
        inbound: stat,
        connection: stats.connection,
      }));

      const outboundVideoStatsByMid = new Map<string, ParsedOutboundVideoStreamCompositeStats[]>();

      stats.video.outbound.forEach((stat) => {
        const remoteInbound = findStatsBySsrc(stat.ssrc, stats.remote.video.inbound);

        if (!remoteInbound) {
          return;
        }

        const compositeStats: ParsedOutboundVideoStreamCompositeStats = {
          outbound: stat,
          remoteInbound,
          connection: stats.connection,
        };

        const group = outboundVideoStatsByMid.get(stat.mid);

        if (group) {
          group.push(compositeStats);
        } else {
          outboundVideoStatsByMid.set(stat.mid, [compositeStats]);
        }
      });

      outboundVideoStatsByMid.forEach((group) => {
        // not all stats items have track object details in WID (usually only the lowest rid stats item)
        const trackIdentifier = group.find(
          (s) => s.outbound.track.trackIdentifier,
        )?.outbound.track.trackIdentifier;

        if (!trackIdentifier) {
          return;
        }

        this.#outboundVideoHandlers.get(trackIdentifier)?.(group);
      });

      stats.audio.outbound.forEach((stat) => {
        const remoteInbound = findStatsBySsrc(stat.ssrc, stats.remote.audio.inbound);

        if (!remoteInbound) {
          return;
        }

        this.#outboundAudioHandlers.get(stat.track.trackIdentifier)?.({
          outbound: stat,
          remoteInbound,
          connection: stats.connection,
        });
      });
    });
  }

  addConnectionHandler(id: string, handler: (stats: ParsedConnectionStats) => void): void {
    this.#connectionHandlers.set(id, handler);
  }

  addInboundVideoHandler(id: string, handler: (stats: ParsedInboundVideoStreamCompositeStats) => void): void {
    this.#inboundVideoHandlers.set(id, handler);
  }

  addInboundAudioHandler(id: string, handler: (stats: ParsedInboundAudioStreamCompositeStats) => void): void {
    this.#inboundAudioHandlers.set(id, handler);
  }

  addOutboundVideoHandler(id: string, handler: (stats: ParsedOutboundVideoStreamCompositeStats[]) => void): void {
    this.#outboundVideoHandlers.set(id, handler);
  }

  addOutboundAudioHandler(id: string, handler: (stats: ParsedOutboundAudioStreamCompositeStats) => void): void {
    this.#outboundAudioHandlers.set(id, handler);
  }

  removeConnectionHandler(id: string): void {
    this.#connectionHandlers.delete(id);
  }

  removeInboundVideoHandler(id: string): void {
    this.#inboundVideoHandlers.delete(id);
  }

  removeInboundAudioHandler(id: string): void {
    this.#inboundAudioHandlers.delete(id);
  }

  removeOutboundVideoHandler(id: string): void {
    this.#outboundVideoHandlers.delete(id);
  }

  removeOutboundAudioHandler(id: string): void {
    this.#outboundAudioHandlers.delete(id);
  }

  clear(): void {
    this.#connectionHandlers.clear();
    this.#inboundVideoHandlers.clear();
    this.#inboundAudioHandlers.clear();
    this.#outboundVideoHandlers.clear();
    this.#outboundAudioHandlers.clear();
  }
}
